import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { COLORS } from "../../../theme/landingTheme";

const FLOW = ["Hypothesis", "Experiment", "Discovery"];

const WORD_BASE_SX = {
  fontWeight: 800,
  lineHeight: 1.1,
  letterSpacing: "0.01em",
  fontSize: { md: "3rem", lg: "3.6rem" },
  userSelect: "none",
};

// Each word gets a distinct typographic treatment.
const WORD_STYLES = [
  // 1) Solid fill
  { color: "rgba(255,255,255,0.24)" },
  // 2) Striped fill (dense, light diagonal stripes clipped to the glyphs)
  {
    color: "transparent",
    WebkitTextFillColor: "transparent",
    backgroundImage:
      "repeating-linear-gradient(45deg, rgba(255,255,255,0.3) 0 1.5px, transparent 1.5px 3px)",
    WebkitBackgroundClip: "text",
    backgroundClip: "text",
  },
  // 3) Hollow outline with a thick border
  {
    color: "transparent",
    WebkitTextFillColor: "transparent",
    WebkitTextStroke: "2.5px rgba(255,255,255,0.4)",
  },
];

/**
 * AuthBrandPanel
 * The branded left half of the auth split-panel: a deep teal gradient with
 * the research journey ("Hypothesis / Experiment / Discovery") as very light
 * stacked words in the top-right corner, with the headline, subheadline, and a
 * titled benefits checklist centered in the panel.
 */
export default function AuthBrandPanel({
  title,
  paragraphs = [],
  bulletsTitle,
  bullets = [],
}) {
  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        minHeight: "100%",
        p: { md: 8, lg: 10 },
        color: "#fff",
        background:
          "linear-gradient(150deg, #2E8E7B 0%, #2A746B 48%, #1F4E55 100%)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Soft glows */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          top: -90,
          right: -90,
          width: 300,
          height: 300,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(255,255,255,0.14), rgba(255,255,255,0))",
          pointerEvents: "none",
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          bottom: -120,
          left: -100,
          width: 320,
          height: 320,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(161,205,115,0.18), rgba(161,205,115,0))",
          pointerEvents: "none",
        }}
      />

      {/* Decoration: research journey, one styled word per line — top-right corner */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          top: { md: 64, lg: 80 },
          right: { md: 64, lg: 80 },
          textAlign: "right",
          zIndex: 0,
        }}
      >
        {FLOW.map((word, i) => (
          <Typography key={word} sx={{ ...WORD_BASE_SX, ...WORD_STYLES[i] }}>
            {word}
          </Typography>
        ))}
      </Box>

      {/* Content, vertically centered in the remaining space */}
      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          py: { md: 4 },
        }}
      >
        {title && (
          <Typography
            sx={{
              fontSize: { md: "2rem" },
              fontWeight: 700,
              lineHeight: 1.2,
              mb: 2,
              color: "#ffffff",
            }}
          >
            {title}
          </Typography>
        )}

        {paragraphs.map((p, i) => (
          <Typography
            key={i}
            sx={{
              color: "rgba(255,255,255,0.9)",
              fontSize: "1rem",
              lineHeight: 1.6,
              mb: 1.25,
              maxWidth: 440,
            }}
          >
            {p}
          </Typography>
        ))}

        {bullets.length > 0 && (
          <Box sx={{ mt: 3 }}>
            {bulletsTitle && (
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "1.5rem",
                  mb: 1.75,
                  color: "#ffffff",
                }}
              >
                {bulletsTitle}
              </Typography>
            )}
            <Stack spacing={1.5}>
              {bullets.map((b, i) => (
                <Stack
                  key={i}
                  direction="row"
                  spacing={1.25}
                  alignItems="flex-start"
                >
                  <CheckCircleRoundedIcon
                    sx={{
                      fontSize: 20,
                      color: COLORS.BACKGROUND.PRIMARY,
                      mt: "2px",
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.92)",
                      fontSize: "0.95rem",
                      lineHeight: 1.5,
                    }}
                  >
                    {b}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        )}
      </Box>
    </Box>
  );
}
